import { useState, useRef } from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import emailjs from '@emailjs/browser';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { motion, useInView } from "framer-motion";

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: formData.name, from_email: formData.email, subject: formData.subject, message: formData.message
      }, EMAILJS_PUBLIC_KEY);
      toast({ title: "Message envoyé !", description: "Nous vous recontacterons dans les plus brefs délais." });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast({ title: "Erreur", description: "Une erreur est survenue. Veuillez réessayer.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    { icon: MapPin, title: "Adresse", details: ["21 Avenue Jean Jaurès", "Dakar, Sénégal"] },
    { icon: Phone, title: "Téléphone", details: ["+33 821 71 27"] },
    { icon: Mail, title: "Email", details: ["contact@adwconseils.com"] },
    { icon: Clock, title: "Horaires", details: ["Lun - Ven : 8h00 - 17h00"] },
  ];

  return (
    <section id="contact" className="py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-[hsl(var(--brand-red))] text-sm font-semibold tracking-widest uppercase mb-3">Parlons de votre projet</span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">Contactez-nous</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discutons de vos besoins et obtenez une proposition personnalisée.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            className="lg:col-span-2 space-y-8"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <div className="grid gap-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-muted/50 border border-border">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-red))] flex items-center justify-center flex-shrink-0">
                    <info.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-sm">{info.title}</h4>
                    {info.details.map((d, i) => <p key={i} className="text-muted-foreground text-sm">{d}</p>)}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl overflow-hidden border border-border shadow-sm">
              <iframe
                src="https://maps.google.com/maps?q=21+Avenue+Jean+Jaures,Dakar,Senegal&z=16&output=embed"
                width="100%" height="200" className="border-0" loading="lazy" allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <div className="rounded-2xl bg-card border border-border p-8 shadow-[var(--shadow-card)]">
              <h3 className="text-xl font-bold text-foreground mb-6">Envoyez-nous un message</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">Nom complet *</label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Votre nom" className="rounded-xl" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">Email *</label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="votre@email.com" className="rounded-xl" />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-1.5">Sujet *</label>
                  <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required placeholder="Sujet de votre demande" className="rounded-xl" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1.5">Message *</label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} placeholder="Décrivez votre projet..." className="rounded-xl" />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-red))] text-white py-6 rounded-xl text-base" disabled={isLoading}>
                  {isLoading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Envoi en cours...</> : <><Send className="h-5 w-5 mr-2" />Envoyer le message</>}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
